import React, { useCallback, useEffect, useState, useRef, memo } from 'react';
import { FadeIn } from './ui/FadeIn';
import { StarIcon, ChevronLeftIcon, ChevronRightIcon, QuoteIcon } from 'lucide-react';
interface Testimonial {
  name: string;
  age: string;
  city: string;
  quote: string;
}
const testimonials: Testimonial[] = [{
  name: 'María G.',
  age: '34 años',
  city: 'Bogotá, Colombia',
  quote: 'Mis hijos esperan cada noche el momento del devocional. Es hermoso ver cómo aprenden de Dios con tanta alegría. Este material cambió nuestras noches en familia.'
}, {
  name: 'Carolina R.',
  age: '29 años',
  city: 'Ciudad de México, México',
  quote: 'Soy maestra de escuela dominical y estos recursos me salvaron. Los niños se emocionan con las actividades y las ilustraciones son preciosas. ¡Lo recomiendo al 100%!'
}, {
  name: 'Valentina M.',
  age: '38 años',
  city: 'Lima, Perú',
  quote: 'Buscaba algo que fuera tierno pero con contenido bíblico real. Esta colección tiene ambas cosas. Mi hija de 5 años ya memoriza versículos gracias al cuaderno.'
}, {
  name: 'Andrea P.',
  age: '31 años',
  city: 'Buenos Aires, Argentina',
  quote: 'Como mamá primeriza no sabía cómo empezar a hablarle de Dios a mi hijo. Este material me dio las herramientas perfectas. Es simple, bonito y lleno de amor.'
}, {
  name: 'Lucía F.',
  age: '62 años',
  city: 'Santiago, Chile',
  quote: 'Cuido a mis nietos los fines de semana y este material nos une. Coloreamos juntos, leemos las historias bíblicas y oramos. No tiene precio lo que genera en ellos.'
}];
function ArrowButton({
  direction,
  onClick,
  disabled
}: {
  direction: 'left' | 'right';
  onClick: () => void;
  disabled: boolean;
}) {
  return <button onClick={onClick} disabled={disabled} aria-label={direction === 'left' ? 'Testimonio anterior' : 'Siguiente testimonio'} className={`
        w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center
        transition-all duration-200 shadow-sm
        ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
      `} style={{
    backgroundColor: 'white',
    border: '1px solid var(--color-border)'
  }} onMouseEnter={e => {
    if (disabled) return;
    e.currentTarget.style.backgroundColor = '#FEF6E8';
    e.currentTarget.style.borderColor = 'var(--color-gold)';
    const icon = e.currentTarget.querySelector('svg');
    if (icon) (icon as HTMLElement).style.color = 'var(--color-gold)';
  }} onMouseLeave={e => {
    e.currentTarget.style.backgroundColor = 'white';
    e.currentTarget.style.borderColor = 'var(--color-border)';
    const icon = e.currentTarget.querySelector('svg');
    if (icon) (icon as HTMLElement).style.color = 'var(--color-deep-blue)';
  }} data-id="element-232">
      {direction === 'left' ? <ChevronLeftIcon className="w-5 h-5" style={{
      color: 'var(--color-deep-blue)'
    }} data-id="element-233" /> : <ChevronRightIcon className="w-5 h-5" style={{
      color: 'var(--color-deep-blue)'
    }} data-id="element-234" />}
    </button>;
}
function TestimonialCard({
  testimonial
}: {
  testimonial: Testimonial;
}) {
  return <div className="bg-white rounded-2xl p-7 md:p-8 shadow-md h-full flex flex-col relative" style={{
    border: '1px solid var(--color-border)'
  }} data-id="element-235">
      {/* Decorative quote icon */}
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{
      backgroundColor: '#FEF6E8'
    }} data-id="element-236">
        <QuoteIcon className="w-5 h-5" style={{
        color: 'var(--color-gold)'
      }} data-id="element-237" />
      </div>

      {/* Stars */}
      <div className="flex gap-1 mb-4" data-id="element-238">
        <StarIcon className="w-4 h-4 fill-current" style={{
        color: 'var(--color-gold)'
      }} data-id="element-239" />
        <StarIcon className="w-4 h-4 fill-current" style={{
        color: 'var(--color-gold)'
      }} data-id="element-240" />
        <StarIcon className="w-4 h-4 fill-current" style={{
        color: 'var(--color-gold)'
      }} data-id="element-241" />
        <StarIcon className="w-4 h-4 fill-current" style={{
        color: 'var(--color-gold)'
      }} data-id="element-242" />
        <StarIcon className="w-4 h-4 fill-current" style={{
        color: 'var(--color-gold)'
      }} data-id="element-243" />
      </div>

      {/* Quote */}
      <p className="text-base md:text-lg italic leading-relaxed mb-6 flex-grow" style={{
      color: 'var(--color-body)'
    }} data-id="element-244">
        &ldquo;{testimonial.quote}&rdquo;
      </p>

      {/* Author */}
      <div className="pt-4" style={{
      borderTop: '1px solid var(--color-border)'
    }} data-id="element-245">
        <p className="font-bold text-base" style={{
        color: 'var(--color-deep-blue)'
      }} data-id="element-246">
          {testimonial.name}
        </p>
        <p className="text-sm mt-0.5" style={{
        color: 'var(--color-soft-blue)'
      }} data-id="element-247">
          {testimonial.age} · {testimonial.city}
        </p>
      </div>
    </div>;
}
export function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(1);
  const trackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      if (w >= 1024) {
        setVisibleCount(3);
      } else if (w >= 768) {
        setVisibleCount(2);
      } else {
        setVisibleCount(1);
      }
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  // Clamp currentIndex when visibleCount changes
  useEffect(() => {
    const maxIdx = Math.max(0, testimonials.length - visibleCount);
    if (currentIndex > maxIdx) {
      setCurrentIndex(maxIdx);
    }
  }, [visibleCount, currentIndex]);
  const maxIndex = Math.max(0, testimonials.length - visibleCount);
  const goNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
  }, [maxIndex]);
  const goPrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  }, []);
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < maxIndex;
  const gapPx = visibleCount === 1 ? 16 : 20;
  const totalDots = maxIndex + 1;
  // Calculate the percentage offset per step
  // Each card takes (100 / visibleCount)% of the container
  // Each step moves by one card width + proportional gap
  const stepPercent = 100 / visibleCount;
  const translateValue = currentIndex * stepPercent;
  return <section className="py-12 md:py-16" style={{
    backgroundColor: 'var(--color-cream)'
  }} data-id="element-248">
      <div className="container mx-auto px-4" data-id="element-249">
        <FadeIn data-id="element-250">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{
          color: 'var(--color-deep-blue)'
        }} data-id="element-251">
            Lo que dicen las familias
          </h2>
        </FadeIn>

        {/* Carousel container */}
        <div className="relative max-w-5xl mx-auto" data-id="element-252">
          {/* Arrow buttons */}
          <div className="absolute -left-2 md:-left-6 top-1/2 -translate-y-1/2 z-20" data-id="element-253">
            <ArrowButton direction="left" onClick={goPrev} disabled={!canGoPrev} data-id="element-254" />
          </div>
          <div className="absolute -right-2 md:-right-6 top-1/2 -translate-y-1/2 z-20" data-id="element-255">
            <ArrowButton direction="right" onClick={goNext} disabled={!canGoNext} data-id="element-256" />
          </div>

          {/* Cards track */}
          <div className="overflow-hidden mx-8 md:mx-10" data-id="element-257">
            <div ref={trackRef} className="flex transition-transform duration-500 ease-out" style={{
            gap: `${gapPx}px`,
            transform: `translateX(calc(-${translateValue}% - ${currentIndex * gapPx}px))`
          }} data-id="element-258">
              {testimonials.map((t, index) => <div key={index} className="flex-shrink-0 transition-opacity duration-500" style={{
              width: `calc(${stepPercent}% - ${(visibleCount - 1) * gapPx / visibleCount}px)`
            }} data-id="element-259">
                  <TestimonialCard testimonial={t} data-id="element-260" />
                </div>)}
            </div>
          </div>
        </div>

        {/* Dots indicator */}
        <div className="flex items-center justify-center gap-2 mt-8" data-id="element-261">
          {Array.from({
          length: totalDots
        }).map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} aria-label={`Ir a página ${index + 1}`} className="transition-all duration-300 rounded-full" style={{
          width: index === currentIndex ? '24px' : '8px',
          height: '8px',
          backgroundColor: index === currentIndex ? 'var(--color-gold)' : 'var(--color-border)'
        }} data-id="element-262" />)}
        </div>
      </div>
    </section>;
}